import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { fetchHistoricalResults } from '@/api/vercel';
import { 
  aggregateRaceDataForFilters, 
  generateFilterOptionsFromAggregation,
  validateFilterCriteria,
  sanitizeFilterInput 
} from '@/utils/filter-processing';
import { useBulkTrackData } from './useBulkTrackData';

/**
 * Hook for fetching bulk historical race data with enhanced filtering and validation
 * Extends existing historical results patterns for bulk analysis
 */
export const useBulkHistoricalData = (filters = {}, options = {}) => {
  const { validateFilters = true, sanitizeInput = true, useTrackByTrack = false } = options;

  // Use track-by-track fetching if requested
  const trackDataQuery = useBulkTrackData(filters, { 
    enabled: useTrackByTrack,
    maxTracks: 30 // Limit to avoid too many API calls
  });

  // Main query for standard historical data
  const mainQuery = useQuery({
    queryKey: ['bulk-historical-data', filters],
    queryFn: async () => {
      try {
        // Sanitize input filters if enabled
        const processedFilters = sanitizeInput ? sanitizeFilterInput(filters) : filters;

        console.log('Starting bulk historical data fetch...');
        
        // Fetch available historical data
        const response = await fetchHistoricalResults({});
        
        if (response.status === 'error') {
          throw new Error(response.message || 'Failed to fetch historical data');
        }

        // Handle different response structures
        let racesArray = [];
        
        if (response.data && typeof response.data === 'object') {
          // If data is an object with race keys, convert to array
          racesArray = Object.entries(response.data).map(([raceId, raceData]) => ({
            ...raceData,
            id: raceId
          }));
        } else if (Array.isArray(response.data)) {
          // If data is already an array, use it directly
          racesArray = response.data;
        } else {
          console.warn('Unexpected data structure from fetchHistoricalResults:', response.data);
          racesArray = [];
        }
        
        console.log(`Fetched ${racesArray.length} races from historical results API`);
        
        // Apply filters if provided
        const filteredRaces = applyFiltersToRaces(racesArray, processedFilters);

        return {
          races: filteredRaces,
          totalCount: filteredRaces.length,
          appliedFilters: processedFilters,
          originalCount: racesArray.length,
          filterEfficiency: racesArray.length > 0 ? (filteredRaces.length / racesArray.length) * 100 : 0,
          dataLimited: racesArray.length === 0,
          source: 'historical-api'
        };
      } catch (error) {
        console.error('Error fetching bulk historical data:', error);
        throw error;
      }
    },
    enabled: !useTrackByTrack, // Only run if not using track-by-track
    staleTime: 10 * 60 * 1000, // 10 minutes for bulk data
    gcTime: 60 * 60 * 1000, // 1 hour cache time for large datasets
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    // Add specific error handling for large datasets
    onError: (error) => {
      console.error('Bulk historical data fetch failed:', error);
    },
    // Optimize for large datasets
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  // Return the appropriate query result
  if (useTrackByTrack) {
    return {
      ...trackDataQuery,
      data: trackDataQuery.data ? {
        ...trackDataQuery.data,
        source: 'track-by-track',
        appliedFilters: sanitizeInput ? sanitizeFilterInput(filters) : filters,
        filterEfficiency: 100 // Track-by-track doesn't apply additional filters
      } : undefined
    };
  }

  return mainQuery;
};

/**
 * Apply filters to races array
 * @param {Array} races - Array of race data
 * @param {Object} filters - Filter criteria
 * @returns {Array} Filtered races
 */
const applyFiltersToRaces = (races, filters) => {
  if (!races || !Array.isArray(races)) {
    return [];
  }

  let filteredRaces = [...races];

  // Apply date range filter
  if (filters.dateRange?.start || filters.dateRange?.end) {
    filteredRaces = filteredRaces.filter(race => {
      if (!race.date) return false;
      const raceDate = new Date(race.date);
      
      if (filters.dateRange.start && raceDate < new Date(filters.dateRange.start)) {
        return false;
      }
      if (filters.dateRange.end && raceDate > new Date(filters.dateRange.end)) {
        return false;
      }
      return true;
    });
  }

  // Apply days of week filter
  if (filters.daysOfWeek && filters.daysOfWeek.length > 0) {
    filteredRaces = filteredRaces.filter(race => {
      if (!race.date) return false;
      const dayOfWeek = new Date(race.date).getDay();
      const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      return filters.daysOfWeek.includes(dayNames[dayOfWeek]);
    });
  }

  // Apply states filter
  if (filters.states && filters.states.length > 0) {
    filteredRaces = filteredRaces.filter(race => {
      return race.state && filters.states.includes(race.state);
    });
  }

  // Apply tracks filter
  if (filters.tracks && filters.tracks.length > 0) {
    filteredRaces = filteredRaces.filter(race => {
      return race.track && filters.tracks.includes(race.track);
    });
  }

  // Apply combined odds threshold filter
  if (filters.combinedOddsThreshold !== null && filters.combinedOddsThreshold !== undefined) {
    filteredRaces = filteredRaces.filter(race => {
      if (!race.tips || !Array.isArray(race.tips)) return false;
      
      const combinedOdds = race.tips.reduce((sum, tip) => {
        const odds = parseFloat(tip.odds) || 0;
        return sum + (odds > 0 ? 1 / odds : 0);
      }, 0);
      
      return combinedOdds <= filters.combinedOddsThreshold;
    });
  }

  // Apply horse count range filter
  if (filters.horseCountRange?.min !== null || filters.horseCountRange?.max !== null) {
    filteredRaces = filteredRaces.filter(race => {
      const horseCount = race.horse_count || race.horses?.length || 0;
      
      if (filters.horseCountRange.min !== null && horseCount < filters.horseCountRange.min) {
        return false;
      }
      if (filters.horseCountRange.max !== null && horseCount > filters.horseCountRange.max) {
        return false;
      }
      return true;
    });
  }

  return filteredRaces;
};

/**
 * Hook for fetching filter options from historical data
 * Extracts available options from the bulk historical dataset using advanced aggregation
 */
export const useFilterOptions = (options = {}) => {
  const {
    includeStats = true,
    sortByFrequency = true,
    minOccurrences = 1,
  } = options;

  return useQuery({
    queryKey: ['filter-options', includeStats, sortByFrequency, minOccurrences],
    queryFn: async () => {
      try {
        // Fetch historical data to extract filter options
        // Note: fetchAll doesn't work, so we'll fetch without it
        const response = await fetchHistoricalResults({});

        if (response.status === 'error') {
          throw new Error(response.message || 'Failed to fetch filter options');
        }

        // Handle different response structures since fetchAll doesn't work
        let racesArray = [];
        
        if (response.data && typeof response.data === 'object') {
          racesArray = Object.values(response.data);
        } else if (Array.isArray(response.data)) {
          racesArray = response.data;
        } else {
          console.warn('Unexpected data structure from fetchHistoricalResults:', response.data);
          racesArray = [];
        }
        
        // Use advanced aggregation functions
        const aggregation = aggregateRaceDataForFilters(racesArray);
        const filterOptions = generateFilterOptionsFromAggregation(aggregation, {
          includeStats,
          sortByFrequency,
          minOccurrences,
        });
        
        return filterOptions;
      } catch (error) {
        console.error('Error fetching filter options:', error);
        
        // Return default options on error
        return {
          availableDates: [],
          availableStates: ['NSW', 'VIC', 'QLD', 'SA', 'WA', 'TAS', 'NT', 'ACT'],
          availableTracks: [],
          availableDaysOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
          availableRaceTypes: [],
          dateRange: {
            earliest: '2020-01-01',
            latest: new Date().toISOString().split('T')[0]
          },
          horseCountRange: {
            min: 4,
            max: 20
          },
          totalRaces: 0,
          dataQuality: { totalRaces: 0, completenessScore: 0 }
        };
      }
    },
    staleTime: 60 * 60 * 1000, // 1 hour
    gcTime: 2 * 60 * 60 * 1000, // 2 hours
    retry: 2,
    retryDelay: 5000,
  });
};

/**
 * Extract filter options from historical race data
 * @param {Array} races - Array of race data
 * @returns {Object} Filter options
 */
const extractFilterOptions = (races) => {
  if (!races || !Array.isArray(races) || races.length === 0) {
    return {
      availableDates: [],
      availableStates: [],
      availableTracks: [],
      dateRange: {
        earliest: new Date().toISOString().split('T')[0],
        latest: new Date().toISOString().split('T')[0]
      },
      horseCountRange: {
        min: 4,
        max: 20
      }
    };
  }

  // Extract unique dates
  const dates = new Set();
  const states = new Set();
  const tracks = new Set();
  let minHorseCount = Infinity;
  let maxHorseCount = -Infinity;
  let earliestDate = null;
  let latestDate = null;

  races.forEach(race => {
    // Process dates
    if (race.date) {
      const dateStr = race.date.split('T')[0]; // Get YYYY-MM-DD format
      dates.add(dateStr);
      
      const raceDate = new Date(dateStr);
      if (!earliestDate || raceDate < earliestDate) {
        earliestDate = raceDate;
      }
      if (!latestDate || raceDate > latestDate) {
        latestDate = raceDate;
      }
    }

    // Process states
    if (race.state) {
      states.add(race.state);
    }

    // Process tracks
    if (race.track) {
      tracks.add(race.track);
    }

    // Process horse counts
    const horseCount = race.horse_count || race.horses?.length || 0;
    if (horseCount > 0) {
      minHorseCount = Math.min(minHorseCount, horseCount);
      maxHorseCount = Math.max(maxHorseCount, horseCount);
    }
  });

  // Convert sets to sorted arrays
  const availableDates = Array.from(dates).sort().reverse(); // Most recent first
  const availableStates = Array.from(states).sort();
  const availableTracks = Array.from(tracks).sort();

  // Handle edge cases for horse count
  if (minHorseCount === Infinity) minHorseCount = 4;
  if (maxHorseCount === -Infinity) maxHorseCount = 20;

  return {
    availableDates,
    availableStates,
    availableTracks,
    dateRange: {
      earliest: earliestDate ? earliestDate.toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
      latest: latestDate ? latestDate.toISOString().split('T')[0] : new Date().toISOString().split('T')[0]
    },
    horseCountRange: {
      min: minHorseCount,
      max: maxHorseCount
    }
  };
};

export default useBulkHistoricalData;
/**

 * Validate and sanitize race data
 * @param {Object} race - Race data object
 * @returns {Object|null} Sanitized race data or null if invalid
 */
const validateAndSanitizeRace = (race) => {
  if (!race || typeof race !== 'object') {
    return null;
  }

  // Required fields validation
  if (!race.id || !race.date) {
    return null;
  }

  // Sanitize and validate date
  const dateRegex = /^\d{4}-\d{2}-\d{2}/;
  if (!dateRegex.test(race.date)) {
    return null;
  }

  // Sanitize numeric fields
  const sanitizedRace = {
    ...race,
    horse_count: parseInt(race.horse_count) || 0,
    race_number: parseInt(race.race_number) || 0,
  };

  // Validate and sanitize tips array
  if (race.tips && Array.isArray(race.tips)) {
    sanitizedRace.tips = race.tips
      .filter(tip => tip && typeof tip === 'object')
      .map(tip => ({
        ...tip,
        odds: parseFloat(tip.odds) || 0,
        position: parseInt(tip.position) || 0,
      }))
      .filter(tip => tip.odds > 0); // Remove invalid odds
  }

  // Validate and sanitize horses array
  if (race.horses && Array.isArray(race.horses)) {
    sanitizedRace.horses = race.horses
      .filter(horse => horse && typeof horse === 'object')
      .map(horse => ({
        ...horse,
        number: parseInt(horse.number) || 0,
        odds: parseFloat(horse.odds) || 0,
      }));
  }

  return sanitizedRace;
};

/**
 * Hook for fetching bulk historical data with enhanced error handling and caching
 * Optimized for large datasets with progressive loading
 */
export const useBulkHistoricalDataOptimized = (filters = {}, options = {}) => {
  const {
    enabled = true,
    chunkSize = 1000, // Process data in chunks
    enableProgressiveLoading = true,
  } = options;

  return useQuery({
    queryKey: ['bulk-historical-data-optimized', filters, chunkSize],
    queryFn: async ({ signal }) => {
      try {
        // Check if request was cancelled
        if (signal?.aborted) {
          throw new Error('Request cancelled');
        }

        // Fetch historical data
        // Note: fetchAll doesn't work, so we'll fetch without it
        const response = await fetchHistoricalResults({});

        if (response.status === 'error') {
          throw new Error(response.message || 'Failed to fetch historical data');
        }

        // Transform and validate data
        // Handle different response structures since fetchAll doesn't work
        let rawRacesArray = [];
        
        if (response.data && typeof response.data === 'object') {
          rawRacesArray = Object.values(response.data);
        } else if (Array.isArray(response.data)) {
          rawRacesArray = response.data;
        } else {
          console.warn('Unexpected data structure from fetchHistoricalResults:', response.data);
          rawRacesArray = [];
        }
        
        const racesArray = rawRacesArray
          .map(validateAndSanitizeRace)
          .filter(Boolean); // Remove null/invalid races

        // Check for cancellation again after data processing
        if (signal?.aborted) {
          throw new Error('Request cancelled');
        }

        // Apply filters
        const filteredRaces = applyFiltersToRaces(racesArray, filters);

        // Process in chunks if enabled
        if (enableProgressiveLoading && filteredRaces.length > chunkSize) {
          // Return first chunk immediately, rest will be loaded progressively
          return {
            races: filteredRaces.slice(0, chunkSize),
            totalCount: filteredRaces.length,
            hasMore: filteredRaces.length > chunkSize,
            nextChunkStart: chunkSize,
            allRaces: filteredRaces, // Keep full dataset for further processing
          };
        }

        return {
          races: filteredRaces,
          totalCount: filteredRaces.length,
          hasMore: false,
          nextChunkStart: 0,
          allRaces: filteredRaces,
        };
      } catch (error) {
        console.error('Error fetching optimized bulk historical data:', error);
        throw error;
      }
    },
    enabled,
    staleTime: 15 * 60 * 1000, // 15 minutes for optimized version
    gcTime: 2 * 60 * 60 * 1000, // 2 hours cache time
    retry: (failureCount, error) => {
      // Don't retry if request was cancelled
      if (error.message === 'Request cancelled') {
        return false;
      }
      return failureCount < 3;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    // Add network error handling
    networkMode: 'online',
  });
};
/**

 * Hook for validating filter criteria against available options
 * @param {Object} filters - Filter criteria to validate
 * @param {Object} filterOptions - Available filter options
 * @returns {Object} Validation result
 */
export const useFilterValidation = (filters, filterOptions) => {
  return useMemo(() => {
    if (!filterOptions || !filters) {
      return {
        isValid: true,
        errors: [],
        warnings: [],
        sanitizedFilters: filters || {}
      };
    }

    return validateFilterCriteria(filters, filterOptions);
  }, [filters, filterOptions]);
};

/**
 * Combined hook that fetches filter options and validates current filters
 * @param {Object} currentFilters - Current filter state
 * @param {Object} options - Hook options
 * @returns {Object} Combined filter options and validation
 */
export const useFilterOptionsWithValidation = (currentFilters = {}, options = {}) => {
  const filterOptionsQuery = useFilterOptions(options);
  const validation = useFilterValidation(currentFilters, filterOptionsQuery.data);

  return {
    ...filterOptionsQuery,
    validation,
    isValidating: filterOptionsQuery.isLoading,
    hasValidationErrors: validation.errors.length > 0,
    hasValidationWarnings: validation.warnings.length > 0,
  };
};